/* eslint-disable quotes */

// ---------- helpers ----------
const clone = (v) => JSON.parse(JSON.stringify(v ?? {}));

const splitMinutes = (m = 0) => {
  const n = Math.max(0, Number(m) || 0);
  return { offset_hour: Math.floor(n / 60), offset_minute: n % 60 };
};
const joinToMinutes = (h = 0, m = 0) => {
  const hh = Math.max(0, Number(h) || 0);
  const mm = Math.max(0, Number(m) || 0);
  return Math.min(120, hh * 60 + mm); // límite 120 por UI
};

// ---------- langs ----------
/**
 * Build per-language object shape like:
 * {
 *   es: { lang: 'es', id: null, title: '', content: '' },
 *   en: { lang: 'en', id: null, title: '', content: '' }
 * }
 */
export function buildLangs(langs = []) {
  const o = {};
  langs.forEach(({ key }) => {
    o[key] = { id: null, lang: key, title: "", subtitle: "" };
  });
  return o;
}

export function buildValidations() {
  return {
    title: "", // falta título en 1+ idiomas
    subtitle: "", // falta subtítulo en 1+ idiomas
    tags: "", // se requiere al menos 1 tag
    age_ranges: "", // se requiere al menos 1 rango de edad
    time: "", // EXACT_TIME sin HH:mm
    offset_minutes: "", // otros modos con offset inválido (<= 0)
  };
}

// ---------- ensure shape (UI) ----------
/**
 * Ensure minimal/default model shape for Alerts module.
 * - Fills defaults
 * - Ensures langs keys
 * - Normalizes arrays
 * - Normalizes tags
 */
export function ensureShape(model = {}, langs = []) {
  const c = clone(model);

  // Core ids
  c.id = c.id ?? null;
  c.mini_guide_id = c.mini_guide_id ?? null;

  // enums
  c.biological_sex = c.biological_sex || "BOTH";
  c.action_type = c.action_type || "NONE";
  c.status = c.status || "PENDING_APPROVAL";

  // Langs (object keyed by lang)
  c.langs = c.langs || buildLangs(langs);
  langs.forEach(({ key }) => {
    if (!c.langs[key]) {
      c.langs[key] = { id: null, lang: key, title: "", subtitle: "" };
    }
    const l = c.langs[key];
    l.id = l.id ?? null;
    l.lang = key;
    l.title = l.title ?? "";
    l.subtitle = l.subtitle ?? "";
  });

  // time-of-day UI fields (modo + helpers)
  // Admitimos:
  //  - legacy: c.time_of_day (string) + offset_minutes / offset_relation / timeHoursAndMinutes
  //  - nuevo:  c.time_of_day = { time, offset_hour, offset_minute, offset_relation }
  let mode = "EXACT_TIME";
  let offset_minutes = Number(c.offset_minutes) || 0;
  let offset_relation = c.offset_relation || "AFTER";
  let timeHM = c.timeHoursAndMinutes || "";

  if (c.time_of_day && typeof c.time_of_day === "object" && c.time_of_day.time) {
    mode = c.time_of_day.time;
    // cuando viene como objeto API
    const oh = Number(c.time_of_day.offset_hour) || 0;
    const om = Number(c.time_of_day.offset_minute) || 0;
    offset_relation = c.time_of_day.offset_relation || "AFTER";

    if (mode === "EXACT_TIME") {
      const hh = String(oh).padStart(2, "0");
      const mm = String(om).padStart(2, "0");
      timeHM = `${hh}:${mm}`;
      offset_minutes = 0;
    } else {
      timeHM = "";
      offset_minutes = joinToMinutes(oh, om);
    }
  } else {
    // legacy string en c.time_of_day
    mode = typeof c.time_of_day === "string" ? c.time_of_day : "EXACT_TIME";
    // (offset_minutes/offset_relation/timeHoursAndMinutes ya arriba)
  }

  c.time_of_day = mode;
  c.offset_minutes = Math.min(120, Math.max(0, offset_minutes));
  c.offset_relation = offset_relation;
  c.timeHoursAndMinutes = timeHM;

  // arrays (UI)
  c.hormonal_imbalances = Array.isArray(c.hormonal_imbalances) ? c.hormonal_imbalances : [];
  // aceptar cualquiera de las dos claves para IDs
  c.nutritional_deficits = Array.isArray(c.nutritional_deficits)
    ? c.nutritional_deficits
    : Array.isArray(c.nutritionalDeficits)
      ? c.nutritionalDeficits
      : [];
  c.digestive_disorders = Array.isArray(c.digestive_disorders) ? c.digestive_disorders : [];
  c.age_ranges = Array.isArray(c.age_ranges) ? c.age_ranges : [];

  // tags UI [{id,name}]
  c.tags = Array.isArray(c.tags) ? [...new Set((c.tags || []).map((t) => (t ? Number(t) : null)).filter(Boolean))] : [];

  // Validations
  c.validations = c.validations || buildValidations();
  Object.keys(buildValidations()).forEach((k) => {
    c.validations[k] = c.validations[k] ?? "";
  });

  return c;
}

// ---------- format to API ----------
export function formatToSend(data = {}) {
  const editMode = !!data.id;

  // ----- LANGS -----
  const langsArray = Object.values(data.langs || {}).map((l) => ({
    id: l.id ?? null,
    lang: l.lang,
    title: l.title ?? "",
    subtitle: l.subtitle ?? "",
  }));

  // ----- TIME OF DAY -----
  const timeOfDayBlock = {
    time: data.time_of_day || "EXACT_TIME",
    offset_hour: null,
    offset_minute: null,
    offset_relation: data.offset_relation || "AFTER",
  };

  if (data.time_of_day === "EXACT_TIME") {
    if (data.timeHoursAndMinutes) {
      const [h, m] = data.timeHoursAndMinutes.split(":").map(Number);
      timeOfDayBlock.offset_hour = isNaN(h) ? 0 : h;
      timeOfDayBlock.offset_minute = isNaN(m) ? 0 : m;
    }
  } else {
    timeOfDayBlock.offset_hour = Math.floor((data.offset_minutes || 0) / 60);
    timeOfDayBlock.offset_minute = (data.offset_minutes || 0) % 60;
  }

  // ----- OUTPUT -----
  const out = {
    id: data.id ?? null,
    mini_guide_id: data.mini_guide_id ?? null,
    biological_sex: data.biological_sex || "BOTH",
    action_type: data.action_type || "NONE",
    time_of_day: timeOfDayBlock,
    status: data.status || "PENDING_APPROVAL",
    langs: langsArray,

    age_ranges: (data.age_ranges || []).map((a) => ({ age_range: a })),
    hormonal_imbalances: (data.hormonal_imbalances || []).map((h) => ({
      name: h,
    })),
    nutritional_deficits: (data.nutritional_deficits || []).map((id) => ({
      nutritional_deficit_id: Number(id),
    })),
    digestives_disorders: (data.digestive_disorders || []).map((id) => ({
      digestive_disorder_id: Number(id),
    })),
    tags: (data.tags || []).map((id) => ({ tag_id: Number(id) })),
  };

  return [out, editMode];
}

// ---------- parse from API → UI ----------
export function parseFromApi(api = {}, langsConf = []) {
  const m = {
    id: api.id ?? null,
    mini_guide_id: api.mini_guide_id ?? null,
    biological_sex: api.biological_sex || "BOTH",
    action_type: api.action_type || "NONE",
    status: api.status || "PENDING_APPROVAL",

    // time_of_day (API objeto) → UI helpers
    time_of_day: "EXACT_TIME",
    timeHoursAndMinutes: "",
    offset_minutes: 0,
    offset_relation: "AFTER",

    // arrays UI
    hormonal_imbalances: [],
    nutritional_deficits: [],
    digestive_disorders: [],
    age_ranges: [],
    tags: [],

    // langs UI (obj)
    langs: buildLangs(langsConf),

    validations: buildValidations(),
  };

  // time_of_day
  if (api.time_of_day && typeof api.time_of_day === "object") {
    const { time, offset_hour, offset_minute, offset_relation } = api.time_of_day;
    const mode = time || "EXACT_TIME";
    m.time_of_day = mode;
    m.offset_relation = offset_relation || "AFTER";

    if (mode === "EXACT_TIME") {
      const hh = String(Number(offset_hour) || 0).padStart(2, "0");
      const mm = String(Number(offset_minute) || 0).padStart(2, "0");
      m.timeHoursAndMinutes = `${hh}:${mm}`;
      m.offset_minutes = 0;
    } else {
      m.timeHoursAndMinutes = "";
      m.offset_minutes = joinToMinutes(offset_hour, offset_minute);
    }
  }

  // langs array → obj
  if (Array.isArray(api.langs)) {
    api.langs.forEach((l) => {
      if (!l || !l.lang) return;
      m.langs[l.lang] = {
        id: l.id ?? null,
        lang: l.lang,
        title: l.title ?? "",
        subtitle: l.subtitle ?? "",
      };
    });
  }

  // age_ranges [{"age_range":...}]
  if (Array.isArray(api.age_ranges)) {
    const flat = api.age_ranges.map((a) => a?.age_range).filter(Boolean);
    // si backend incluye ALL + otros, puedes decidir comprimir a ["ALL"]; aquí respetamos lo que venga
    m.age_ranges = flat;
  }

  // hormonales [{name}]
  if (Array.isArray(api.hormonal_imbalances)) {
    m.hormonal_imbalances = api.hormonal_imbalances.map((h) => h?.name).filter(Boolean);
  }

  // deficits [{nutritional_deficit_id}]
  if (Array.isArray(api.nutritional_deficits)) {
    m.nutritional_deficits = api.nutritional_deficits.map((d) => d?.nutritional_deficit_id).filter((v) => v != null);
  }

  // digestivos: soporta "digestives_disorders" ó "digestive_disorders"
  const dig = Array.isArray(api.digestives_disorders)
    ? api.digestives_disorders
    : Array.isArray(api.digestive_disorders)
      ? api.digestive_disorders
      : [];
  m.digestive_disorders = dig.map((d) => d?.digestive_disorder_id).filter((v) => v != null);

  // tags [{tag_id}]
  if (Array.isArray(api.tags)) {
    m.tags = api.tags
      .map((t) => {
        if (!t) return null;

        const id = t.id ?? t.tag_id;
        return Number(id);
      })
      .filter(Boolean);
  }

  return m;
}

/**
 * Valida y setea mensajes en model.validations.
 * Devuelve true si TODO es válido.
 *
 * Reglas:
 * - title: requerido para TODOS los idiomas
 * - subtitle: requerido para TODOS los idiomas
 * - tags: al menos 1
 * - age_ranges: al menos 1 (["ALL"] cuenta)
 * - time_of_day:
 *    * EXACT_TIME  -> timeHoursAndMinutes requerido ("HH:mm")
 *    * otros       -> offset_minutes > 0 (y dentro de 0..120 por UI)
 */
export function validateAlert(model, langs = [], { requireTags = true, requireAgeRanges = true, t = (k) => k } = {}) {
  // Reset
  model.validations = buildValidations();

  // --- 1) Por idioma: title + subtitle (requeridos + longitudes)
  const missingTitle = [];
  const missingSubtitle = [];
  const tooLongTitle = [];
  const tooLongSubtitle = [];
  const MAX_TITLE = 80;
  const MAX_SUBTITLE = 100;

  langs.forEach(({ key }) => {
    const L = model.langs?.[key] || {};
    const title = String(L.title ?? "");
    const subtitle = String(L.subtitle ?? "");

    if (!title.trim()) missingTitle.push(key);
    if (!subtitle.trim()) missingSubtitle.push(key);

    if (title.length > MAX_TITLE) tooLongTitle.push(key);
    if (subtitle.length > MAX_SUBTITLE) tooLongSubtitle.push(key);
  });

  if (missingTitle.length > 0) {
    const names = missingTitle.map((k) => t(`components.langs.${k}.name`)).join(", ");
    model.validations.title = `${t("views.alerts.form.title.validation")} (${names})`;
  }
  if (missingSubtitle.length > 0) {
    const names = missingSubtitle.map((k) => t(`components.langs.${k}.name`)).join(", ");
    model.validations.subtitle = `${t("views.alerts.form.subtitle.validation")} (${names})`;
  }

  if (tooLongTitle.length > 0) {
    const names = tooLongTitle.map((k) => t(`components.langs.${k}.name`)).join(", ");
    const msg = `${t("views.alerts.form.title.maxlength", {
      max: MAX_TITLE,
    })} (${names})`;
    model.validations.title = model.validations.title ? `${model.validations.title} · ${msg}` : msg;
  }
  if (tooLongSubtitle.length > 0) {
    const names = tooLongSubtitle.map((k) => t(`components.langs.${k}.name`)).join(", ");
    const msg = `${t("views.alerts.form.subtitle.maxlength", {
      max: MAX_SUBTITLE,
    })} (${names})`;
    model.validations.subtitle = model.validations.subtitle ? `${model.validations.subtitle} · ${msg}` : msg;
  }

  // --- 2) Tags (>=1)
  if (requireTags) {
    const tagsOk = Array.isArray(model.tags) && model.tags.length > 0;
    if (!tagsOk) {
      model.validations.tags = t("views.alerts.form.tags.validation");
    }
  }

  // --- 3) Age ranges (>=1) — ["ALL"] cuenta como válido
  if (requireAgeRanges) {
    const arr = Array.isArray(model.age_ranges) ? model.age_ranges : [];
    const ageOk = arr.length > 0;
    if (!ageOk) {
      model.validations.age_ranges = t("views.alerts.form.age_range.validation");
    }
  }

  // --- 4) Time-of-day
  const mode = model.time_of_day || "EXACT_TIME";
  if (mode === "EXACT_TIME") {
    const HHMM = String(model.timeHoursAndMinutes || "").trim();
    // formato muy básico: "HH:mm" y números válidos
    let ok = false;
    if (/^\d{2}:\d{2}$/.test(HHMM)) {
      const [h, m] = HHMM.split(":").map((n) => Number(n));
      ok = Number.isFinite(h) && Number.isFinite(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59;
    }
    if (!ok) {
      model.validations.time = t("views.alerts.form.time.validation");
    }
  } else {
    // Otros modos → offset_minutes > 0
    const off = Number(model.offset_minutes) || 0;
    if (!(off > 0)) {
      model.validations.offset_minutes = t("views.alerts.form.time.offset_minutes.validation");
    }
  }

  // --- helper sin-errores
  const isEmpty = (obj = {}) => Object.values(obj).every((v) => !v || String(v).trim() === "");
  return isEmpty(model.validations);
}
